/**
 * Alshallal Pastry Company HR System - Custom Fields Management Page
 * Phase 18.8.1: Dynamic employee fields for organization-specific data
 * 
 * Access: Super Admin, HR Manager (full), HR Admin (read-only)
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Settings,
  List,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DATA_TYPES = [
  { value: 'text', label: 'Text', labelAr: 'نص' },
  { value: 'number', label: 'Number', labelAr: 'رقم' },
  { value: 'date', label: 'Date', labelAr: 'تاريخ' },
  { value: 'boolean', label: 'Yes/No', labelAr: 'نعم/لا' },
  { value: 'dropdown', label: 'Dropdown', labelAr: 'قائمة منسدلة' },
];

const CustomFieldsPage = () => {
  const { token, user, hasRole } = useAuth();
  const { isRTL } = useLanguage();
  
  // State
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  
  // Dialog state
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, field: null });
  const [deactivateDialog, setDeactivateDialog] = useState({ open: false, field: null });
  
  // Form state
  const [formData, setFormData] = useState({
    key: '',
    label_en: '',
    label_ar: '',
    data_type: 'text',
    dropdown_options: '',
    description: '',
    is_required: false
  });
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // RBAC
  const canManage = hasRole('super_admin', 'hr_manager');
  const canView = hasRole('super_admin', 'hr_manager', 'hr_admin');
  
  // Fetch custom fields
  const fetchFields = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${API_URL}/custom-fields/?include_inactive=${showInactive}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFields(response.data.fields || []);
    } catch (err) {
      console.error('Failed to fetch custom fields:', err);
      setError(isRTL ? 'فشل في تحميل الحقول المخصصة' : 'Failed to load custom fields');
    } finally {
      setLoading(false);
    }
  }, [token, showInactive, isRTL]);
  
  useEffect(() => {
    if (canView) {
      fetchFields();
    }
  }, [canView, fetchFields]);
  
  // Reset form
  const resetForm = () => {
    setFormData({
      key: '',
      label_en: '',
      label_ar: '',
      data_type: 'text',
      dropdown_options: '',
      description: '',
      is_required: false
    });
    setFormError(null);
  };
  
  // Generate key from label
  const generateKey = (label) => {
    return label
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 50);
  };
  
  // Handle create
  const handleCreate = async () => {
    try {
      setSaving(true);
      setFormError(null);
      
      // Validate
      if (!formData.key || !formData.label_en) {
        setFormError(isRTL ? 'المفتاح والاسم الإنجليزي مطلوبان' : 'Key and English label are required');
        return;
      }
      
      // Parse dropdown options
      let dropdownOptions = null;
      if (formData.data_type === 'dropdown' && formData.dropdown_options) {
        dropdownOptions = formData.dropdown_options
          .split('\n')
          .map(opt => opt.trim())
          .filter(opt => opt);
        if (dropdownOptions.length === 0) {
          setFormError(isRTL ? 'حقول القائمة المنسدلة تحتاج خيار واحد على الأقل' : 'Dropdown fields need at least one option');
          return;
        }
      }
      
      const payload = {
        key: formData.key.toLowerCase(),
        label_en: formData.label_en,
        label_ar: formData.label_ar || null,
        data_type: formData.data_type,
        dropdown_options: dropdownOptions,
        description: formData.description || null,
        is_required: formData.is_required
      };
      
      await axios.post(`${API_URL}/custom-fields/`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCreateDialog(false);
      resetForm();
      fetchFields();
    } catch (err) {
      console.error('Failed to create field:', err);
      setFormError(err.response?.data?.detail || (isRTL ? 'فشل في إنشاء الحقل' : 'Failed to create field'));
    } finally {
      setSaving(false);
    }
  };
  
  // Handle update
  const handleUpdate = async () => {
    try {
      setSaving(true);
      setFormError(null);
      
      const field = editDialog.field;
      
      // Parse dropdown options
      let dropdownOptions = null;
      if (field.data_type === 'dropdown' && formData.dropdown_options) {
        dropdownOptions = formData.dropdown_options
          .split('\n')
          .map(opt => opt.trim())
          .filter(opt => opt);
      }
      
      const payload = {
        label_en: formData.label_en,
        label_ar: formData.label_ar || null,
        dropdown_options: dropdownOptions,
        description: formData.description || null,
        is_required: formData.is_required
      };
      
      await axios.patch(`${API_URL}/custom-fields/${field.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEditDialog({ open: false, field: null });
      resetForm();
      fetchFields();
    } catch (err) {
      console.error('Failed to update field:', err);
      setFormError(err.response?.data?.detail || (isRTL ? 'فشل في تحديث الحقل' : 'Failed to update field'));
    } finally {
      setSaving(false);
    }
  };
  
  // Handle deactivate/reactivate
  const handleToggleActive = async (field, activate) => {
    try {
      const endpoint = activate ? 'reactivate' : 'deactivate';
      await axios.post(`${API_URL}/custom-fields/${field.id}/${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeactivateDialog({ open: false, field: null });
      fetchFields();
    } catch (err) {
      console.error('Failed to toggle field status:', err);
      setError(err.response?.data?.detail || (isRTL ? 'فشل في تغيير حالة الحقل' : 'Failed to change field status'));
    }
  };
  
  // Open edit dialog
  const openEditDialog = (field) => {
    setFormData({
      key: field.key,
      label_en: field.label_en,
      label_ar: field.label_ar || '',
      data_type: field.data_type,
      dropdown_options: (field.dropdown_options || []).join('\n'),
      description: field.description || '',
      is_required: field.is_required || false
    });
    setEditDialog({ open: true, field });
  };
  
  // Access denied
  if (!canView) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">
          {isRTL ? 'غير مصرح' : 'Access Denied'}
        </h2>
        <p className="text-slate-600">
          {isRTL 
            ? 'ليس لديك صلاحية للوصول إلى هذه الصفحة'
            : 'You do not have permission to access this page'}
        </p>
      </div>
    );
  }
  
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isRTL ? 'الحقول المخصصة' : 'Custom Fields'}
          </h1>
          <p className="text-slate-600 mt-1">
            {isRTL 
              ? 'إدارة الحقول الإضافية للموظفين الخاصة بمنظمتك'
              : 'Manage organization-specific employee fields'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowInactive(!showInactive)}
          >
            <List className="h-4 w-4 me-2" />
            {showInactive 
              ? (isRTL ? 'إخفاء غير النشطة' : 'Hide Inactive')
              : (isRTL ? 'إظهار الكل' : 'Show All')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchFields}
            disabled={loading}
          >
            <RefreshCw className={cn("h-4 w-4 me-2", loading && "animate-spin")} />
            {isRTL ? 'تحديث' : 'Refresh'}
          </Button>
          {canManage && (
            <Button onClick={() => setCreateDialog(true)}>
              <Plus className="h-4 w-4 me-2" />
              {isRTL ? 'إضافة حقل' : 'Add Field'}
            </Button>
          )}
        </div>
      </div>
      
      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      
      {/* Fields Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            {isRTL ? 'قائمة الحقول' : 'Fields List'}
          </CardTitle>
          <CardDescription>
            {isRTL 
              ? `${fields.length} حقل مخصص`
              : `${fields.length} custom field${fields.length !== 1 ? 's' : ''}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : fields.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              {isRTL 
                ? 'لا توجد حقول مخصصة بعد'
                : 'No custom fields yet'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{isRTL ? 'المفتاح' : 'Key'}</TableHead>
                  <TableHead>{isRTL ? 'الاسم (EN)' : 'Label (EN)'}</TableHead>
                  <TableHead>{isRTL ? 'الاسم (AR)' : 'Label (AR)'}</TableHead>
                  <TableHead>{isRTL ? 'النوع' : 'Type'}</TableHead>
                  <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                  {canManage && <TableHead className="text-end">{isRTL ? 'إجراءات' : 'Actions'}</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {fields.map((field) => (
                  <TableRow key={field.id} className={!field.is_active ? 'opacity-50' : ''}>
                    <TableCell className="font-mono text-sm">{field.key}</TableCell>
                    <TableCell>{field.label_en}</TableCell>
                    <TableCell>{field.label_ar || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {DATA_TYPES.find(t => t.value === field.data_type)?.[isRTL ? 'labelAr' : 'label'] || field.data_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {field.is_active ? (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="h-3 w-3 me-1" />
                          {isRTL ? 'نشط' : 'Active'}
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          <XCircle className="h-3 w-3 me-1" />
                          {isRTL ? 'غير نشط' : 'Inactive'}
                        </Badge>
                      )}
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-end">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(field)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeactivateDialog({ open: true, field })}
                          >
                            {field.is_active ? (
                              <XCircle className="h-4 w-4 text-amber-500" />
                            ) : (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'إضافة حقل مخصص' : 'Add Custom Field'}</DialogTitle>
            <DialogDescription>
              {isRTL 
                ? 'أنشئ حقلاً جديداً لتخزين بيانات إضافية للموظفين'
                : 'Create a new field to store additional employee data'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {formError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                {formError}
              </div>
            )}
            
            <div className="space-y-2">
              <Label>{isRTL ? 'الاسم (English)' : 'Label (English)'} *</Label>
              <Input
                value={formData.label_en}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    label_en: val,
                    key: prev.key || generateKey(val)
                  }));
                }}
                placeholder="e.g., Allocation"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{isRTL ? 'الاسم (العربية)' : 'Label (Arabic)'}</Label>
              <Input
                value={formData.label_ar}
                onChange={(e) => setFormData(prev => ({ ...prev, label_ar: e.target.value }))}
                placeholder="مثال: التخصيص"
                dir="rtl"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{isRTL ? 'المفتاح' : 'Key'} *</Label>
              <Input
                value={formData.key}
                onChange={(e) => setFormData(prev => ({ ...prev, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
                placeholder="e.g., allocation"
                className="font-mono"
              />
              <p className="text-xs text-slate-500">
                {isRTL ? 'حروف إنجليزية صغيرة وأرقام وشرطات سفلية فقط' : 'Lowercase letters, numbers, and underscores only'}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>{isRTL ? 'نوع البيانات' : 'Data Type'}</Label>
              <Select
                value={formData.data_type}
                onValueChange={(val) => setFormData(prev => ({ ...prev, data_type: val }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATA_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {isRTL ? type.labelAr : type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {formData.data_type === 'dropdown' && (
              <div className="space-y-2">
                <Label>{isRTL ? 'الخيارات' : 'Options'}</Label>
                <Textarea
                  value={formData.dropdown_options}
                  onChange={(e) => setFormData(prev => ({ ...prev, dropdown_options: e.target.value }))}
                  placeholder={isRTL ? 'خيار واحد لكل سطر' : 'One option per line'}
                  rows={4}
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label>{isRTL ? 'الوصف' : 'Description'}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={isRTL ? 'وصف اختياري للحقل' : 'Optional field description'}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialog(false); resetForm(); }}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
              {isRTL ? 'إنشاء' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => { if (!open) { setEditDialog({ open: false, field: null }); resetForm(); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'تعديل الحقل' : 'Edit Field'}</DialogTitle>
            <DialogDescription>
              {isRTL ? 'تعديل تفاصيل الحقل المخصص' : 'Modify custom field details'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {formError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                {formError}
              </div>
            )}
            
            <div className="space-y-2">
              <Label>{isRTL ? 'المفتاح' : 'Key'}</Label>
              <Input value={formData.key} disabled className="font-mono bg-slate-50" />
              <p className="text-xs text-slate-500">
                {isRTL ? 'لا يمكن تغيير المفتاح بعد الإنشاء' : 'Key cannot be changed after creation'}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>{isRTL ? 'الاسم (English)' : 'Label (English)'} *</Label>
              <Input
                value={formData.label_en}
                onChange={(e) => setFormData(prev => ({ ...prev, label_en: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>{isRTL ? 'الاسم (العربية)' : 'Label (Arabic)'}</Label>
              <Input
                value={formData.label_ar}
                onChange={(e) => setFormData(prev => ({ ...prev, label_ar: e.target.value }))}
                dir="rtl"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{isRTL ? 'نوع البيانات' : 'Data Type'}</Label>
              <Input 
                value={DATA_TYPES.find(t => t.value === editDialog.field?.data_type)?.[isRTL ? 'labelAr' : 'label'] || formData.data_type} 
                disabled 
                className="bg-slate-50" 
              />
              <p className="text-xs text-slate-500">
                {isRTL ? 'لا يمكن تغيير نوع البيانات بعد الإنشاء' : 'Data type cannot be changed after creation'}
              </p>
            </div>
            
            {editDialog.field?.data_type === 'dropdown' && (
              <div className="space-y-2">
                <Label>{isRTL ? 'الخيارات' : 'Options'}</Label>
                <Textarea
                  value={formData.dropdown_options}
                  onChange={(e) => setFormData(prev => ({ ...prev, dropdown_options: e.target.value }))}
                  placeholder={isRTL ? 'خيار واحد لكل سطر' : 'One option per line'}
                  rows={4}
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label>{isRTL ? 'الوصف' : 'Description'}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditDialog({ open: false, field: null }); resetForm(); }}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={handleUpdate} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
              {isRTL ? 'حفظ' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Deactivate/Reactivate Dialog */}
      <Dialog open={deactivateDialog.open} onOpenChange={(open) => !open && setDeactivateDialog({ open: false, field: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {deactivateDialog.field?.is_active 
                ? (isRTL ? 'تعطيل الحقل' : 'Deactivate Field')
                : (isRTL ? 'تفعيل الحقل' : 'Reactivate Field')}
            </DialogTitle>
            <DialogDescription>
              {deactivateDialog.field?.is_active 
                ? (isRTL 
                    ? 'سيتم إخفاء هذا الحقل من التصدير والاستيراد. البيانات الموجودة ستبقى محفوظة.'
                    : 'This field will be hidden from exports and imports. Existing data will be preserved.')
                : (isRTL 
                    ? 'سيظهر هذا الحقل مرة أخرى في التصدير والاستيراد.'
                    : 'This field will appear again in exports and imports.')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateDialog({ open: false, field: null })}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              variant={deactivateDialog.field?.is_active ? 'destructive' : 'default'}
              onClick={() => handleToggleActive(deactivateDialog.field, !deactivateDialog.field?.is_active)}
            >
              {deactivateDialog.field?.is_active 
                ? (isRTL ? 'تعطيل' : 'Deactivate')
                : (isRTL ? 'تفعيل' : 'Reactivate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomFieldsPage;
